import React, { useState, useEffect } from 'react';
import apiService from '../../apiService';

// Lightweight chart components (no external dependency needed)
// If recharts is installed, you can swap these for Recharts components

function SimpleBarChart({ data, dataKey, labelKey, color = '#FFB900', height = 200 }) {
  if (!data || data.length === 0) return <div style={{height,display:'flex',alignItems:'center',justifyContent:'center',color:'#9AA0A8',fontSize:'0.875rem'}}>Aucune donnée</div>;
  const max = Math.max(...data.map(d => d[dataKey] || 0), 1);
  return (
    <div style={{height,display:'flex',alignItems:'flex-end',gap:'4px',padding:'0.5rem 0'}}>
      {data.map((d, i) => (
        <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:'4px'}}>
          <div style={{
            width:'100%',maxWidth:'40px',borderRadius:'6px 6px 0 0',background:color,
            height:`${Math.max((d[dataKey]/max)*height*0.8, 4)}px`,transition:'height 0.3s',minHeight:'4px'
          }} title={`${d[labelKey]}: ${(d[dataKey] || 0).toLocaleString()}`}/>
          <span style={{fontSize:'0.6rem',color:'#9AA0A8',writingMode:'vertical-lr',transform:'rotate(180deg)',maxHeight:'50px',overflow:'hidden'}}>
            {d[labelKey]?.slice(-5) || ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function SimplePieChart({ data, colors = ['#FFB900','#FF7800','#10b981','#DC2626'], size = 160 }) {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  let cumPercent = 0;
  const getCoord = (percent) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  return (
    <div style={{display:'flex',alignItems:'center',gap:'1.5rem'}}>
      <svg width={size} height={size} viewBox="-1.1 -1.1 2.2 2.2" style={{transform:'rotate(-90deg)'}}>
        {data.map((d, i) => {
          const percent = d.value / total;
          const startPercent = cumPercent;
          cumPercent += percent;
          const [x1, y1] = getCoord(startPercent);
          const [x2, y2] = getCoord(cumPercent);
          const largeArc = percent > 0.5 ? 1 : 0;
          if (percent === 0) return null;
          return (
            <path key={i}
              d={`M ${x1} ${y1} A 1 1 0 ${largeArc} 1 ${x2} ${y2} L 0 0`}
              fill={colors[i % colors.length]} stroke="white" strokeWidth="0.04"
            />
          );
        })}
        <circle cx="0" cy="0" r="0.55" fill="white"/>
      </svg>
      <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
        {data.map((d, i) => (
          <div key={i} style={{display:'flex',alignItems:'center',gap:'0.5rem',fontSize:'0.875rem'}}>
            <div style={{width:'12px',height:'12px',borderRadius:'3px',background:colors[i % colors.length]}}/>
            <span style={{color:'#414852'}}>{d.name}: <strong>{d.value}</strong> ({total>0 ? Math.round(d.value/total*100) : 0}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DeliveryStatsPage() {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiService.get(`/admin/delivery/stats?period=${period}`, { userType: 'admin' })
      .then(response => { if (response.success) setStats(response.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'300px'}}><span style={{fontSize:'1.1rem',color:'#9AA0A8'}}>⏳ Chargement...</span></div>;

  const cards = [
    { label: 'Commandes totales', value: stats?.total_orders || 0, emoji: '📦', color: '#FF7800' },
    { label: 'Livrées', value: stats?.delivered_orders || 0, emoji: '✅', color: '#10B981' },
    { label: 'En cours', value: stats?.active_orders || 0, emoji: '🚚', color: '#FF7800' },
    { label: 'Fournisseurs', value: stats?.total_suppliers || 0, emoji: '🏪', color: '#FFB900' },
    { label: 'Livreurs', value: stats?.total_drivers || 0, emoji: '🛵', color: '#06b6d4' },
    { label: 'Revenus (FC)', value: (stats?.total_revenue || 0).toLocaleString(), emoji: '💰', color: '#10b981' },
  ];

  const ordersByStatus = [
    { name: 'En attente', value: stats?.pending_orders || 0 },
    { name: 'En cours', value: stats?.active_orders || 0 },
    { name: 'Livrées', value: stats?.delivered_orders || 0 },
    { name: 'Annulées', value: stats?.cancelled_orders || 0 },
  ];

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
        <div>
          <h2 style={{margin:0,fontSize:'1.5rem',fontWeight:'700',color:'#1E2229'}}>📊 Statistiques Livraison</h2>
          <p style={{margin:'0.25rem 0 0',fontSize:'0.875rem',color:'#9AA0A8'}}>Analyses et rapports</p>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{
          background:'white',border:'1px solid #E9EBEF',borderRadius:'12px',padding:'0.5rem 0.75rem',fontSize:'0.875rem',cursor:'pointer',outline:'none'
        }}>
          <option value="week">7 jours</option>
          <option value="month">30 jours</option>
          <option value="year">12 mois</option>
        </select>
      </div>

      {/* KPI Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))',gap:'1rem',marginBottom:'1.5rem'}}>
        {cards.map(card => (
          <div key={card.label} style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
              <span style={{fontSize:'1.5rem'}}>{card.emoji}</span>
            </div>
            <p style={{margin:0,fontSize:'1.5rem',fontWeight:'700',color:'#1E2229'}}>{card.value}</p>
            <p style={{margin:'0.25rem 0 0',fontSize:'0.75rem',color:'#9AA0A8'}}>{card.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(400px, 1fr))',gap:'1rem',marginBottom:'1.5rem'}}>
        {/* Orders trend */}
        <div style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
          <h3 style={{margin:'0 0 1rem',fontWeight:'600',fontSize:'1rem',color:'#1E2229'}}>Évolution des commandes</h3>
          <SimpleBarChart data={stats?.daily_orders || []} dataKey="count" labelKey="date" color="#FF7800" height={200}/>
        </div>

        {/* Revenue trend */}
        <div style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
          <h3 style={{margin:'0 0 1rem',fontWeight:'600',fontSize:'1rem',color:'#1E2229'}}>Revenus (FC)</h3>
          <SimpleBarChart data={stats?.daily_revenue || []} dataKey="amount" labelKey="date" color="#10b981" height={200}/>
        </div>

        {/* Status pie */}
        <div style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
          <h3 style={{margin:'0 0 1rem',fontWeight:'600',fontSize:'1rem',color:'#1E2229'}}>Répartition des commandes</h3>
          <SimplePieChart data={ordersByStatus}/>
        </div>

        {/* Top suppliers */}
        <div style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
          <h3 style={{margin:'0 0 1rem',fontWeight:'600',fontSize:'1rem',color:'#1E2229'}}>Top Fournisseurs</h3>
          <div style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
            {(stats?.top_suppliers || []).length === 0 && (
              <p style={{textAlign:'center',color:'#9AA0A8',padding:'2rem 0',fontSize:'0.875rem'}}>Aucune donnée disponible</p>
            )}
            {(stats?.top_suppliers || []).slice(0,5).map((s, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                <span style={{
                  width:'28px',height:'28px',borderRadius:'50%',background:'#fff7ed',color:'#FFB900',
                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.75rem',fontWeight:'700'
                }}>{i+1}</span>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontSize:'0.875rem',fontWeight:'600',color:'#1E2229'}}>{s.name}</p>
                  <p style={{margin:0,fontSize:'0.75rem',color:'#9AA0A8'}}>{s.orders_count} commandes</p>
                </div>
                <span style={{fontWeight:'600',fontSize:'0.875rem',color:'#1E2229'}}>{(s.revenue || 0).toLocaleString()} FC</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent orders table */}
      <div style={{background:'white',borderRadius:'16px',padding:'1.25rem',boxShadow:'0 1px 3px rgba(0,0,0,0.08)'}}>
        <h3 style={{margin:'0 0 1rem',fontWeight:'600',fontSize:'1rem',color:'#1E2229'}}>Commandes récentes</h3>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.875rem'}}>
            <thead>
              <tr style={{borderBottom:'1px solid #F4F5F7'}}>
                <th style={{textAlign:'left',padding:'0.75rem 0.5rem',fontWeight:'600',color:'#9AA0A8'}}>N°</th>
                <th style={{textAlign:'left',padding:'0.75rem 0.5rem',fontWeight:'600',color:'#9AA0A8'}}>Client</th>
                <th style={{textAlign:'left',padding:'0.75rem 0.5rem',fontWeight:'600',color:'#9AA0A8'}}>Montant</th>
                <th style={{textAlign:'left',padding:'0.75rem 0.5rem',fontWeight:'600',color:'#9AA0A8'}}>Statut</th>
                <th style={{textAlign:'left',padding:'0.75rem 0.5rem',fontWeight:'600',color:'#9AA0A8'}}>Date</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.recent_orders || []).map(order => (
                <tr key={order.id} style={{borderBottom:'1px solid #FFFFFF'}}>
                  <td style={{padding:'0.75rem 0.5rem',fontWeight:'600'}}>#{order.order_number}</td>
                  <td style={{padding:'0.75rem 0.5rem',color:'#5C636E'}}>{order.client_name}</td>
                  <td style={{padding:'0.75rem 0.5rem',fontWeight:'600'}}>{(order.total_amount || 0).toLocaleString()} FC</td>
                  <td style={{padding:'0.75rem 0.5rem'}}>
                    <span style={{
                      padding:'0.2rem 0.5rem',borderRadius:'8px',fontSize:'0.75rem',fontWeight:'600',
                      background: order.status === 'delivered' ? '#ECFDF5' : order.status === 'cancelled' ? '#fecaca' : '#fef9c3',
                      color: order.status === 'delivered' ? '#10B981' : order.status === 'cancelled' ? '#dc2626' : '#ca8a04'
                    }}>{order.status}</span>
                  </td>
                  <td style={{padding:'0.75rem 0.5rem',color:'#9AA0A8'}}>{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
